// src/pages/PlatformAdmin.js
import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  getDocs,
  orderBy,
  query,
  doc,
  updateDoc,
} from "firebase/firestore";

const PLATFORM_ADMIN_EMAILS = [
  "mjnayr@yahoo.com",
  "rmelhus@nlcschool.org",
];

const PlatformAdmin = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workingSchoolId, setWorkingSchoolId] = useState("");
  const [error, setError] = useState("");

  const currentEmail = auth.currentUser?.email?.toLowerCase() || "";
  const isAllowed = PLATFORM_ADMIN_EMAILS.includes(currentEmail);

  const loadSchools = async () => {
    setLoading(true);
    setError("");
    try {
      const q = query(collection(db, "schools"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);

      const rows = await Promise.all(
        snap.docs.map(async (schoolDoc) => {
          const schoolId = schoolDoc.id;
          const data = schoolDoc.data();

          const usersSnap = await getDocs(collection(db, "users"));
          const schoolUsers = usersSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((u) => u.schoolId === schoolId);

          const students = schoolUsers.filter((u) => u.role === "student").length;
          const staff = schoolUsers.filter(
            (u) => u.role === "staff" || u.role === "admin"
          ).length;

          let ticketsCount = 0;
          try {
            const ticketsSnap = await getDocs(
              collection(db, "schools", schoolId, "tickets")
            );
            ticketsCount = ticketsSnap.size;
          } catch {
            ticketsCount = 0;
          }

          return {
            id: schoolId,
            ...data,
            students,
            staff,
            ticketsCount,
          };
        })
      );

      setSchools(rows);
    } catch (err) {
      console.error("Failed to load schools:", err);
      setError(err.message || "Failed to load schools.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSchools();
  }, []);
  const togglePaymentStatus = async (schoolId, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";
  
      await updateDoc(doc(db, "schools", schoolId), {
        paymentStatus: newStatus,
      });
  
      await loadSchools(); // refresh UI
    } catch (err) {
      console.error("Error updating payment status:", err);
      alert("Failed to update payment status");
    }
  };

  const handleDeleteSchool = async (school) => {
    if (!isAllowed) {
      alert("You do not have access to this page.");
      return;
    }

    const confirmText = `${school.id}`;
    const typed = window.prompt(
      `Delete school "${school.name}"?\n\nThis removes:\n- school document\n- school tickets/store/pbis values/etc.\n- all users tied to this school\n- all Firebase Auth accounts tied to this school\n\nType the school ID to confirm:\n\n${confirmText}`
    );

    if (typed !== confirmText) {
      alert("Deletion cancelled.");
      return;
    }

    try {
      setWorkingSchoolId(school.id);

      const idToken = await auth.currentUser.getIdToken(true);

      const response = await fetch(
        "https://us-central1-pbis-app-72ba5.cloudfunctions.net/deleteSchoolHttp",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ schoolId: school.id }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to delete school.");
      }

      alert(`✅ Deleted ${school.name}`);
      await loadSchools();
    } catch (err) {
      console.error("Delete school failed:", err);
      alert(`❌ Failed to delete school: ${err.message}`);
    } finally {
      setWorkingSchoolId("");
    }
  };

  const summary = useMemo(() => {
    return {
      totalSchools: schools.length,
      activeSchools: schools.filter((s) => s.status === "active").length,
      paidSchools: schools.filter((s) => s.paymentStatus === "active").length,
    };
  }, [schools]);

  if (!isAllowed) {
    return (
      <div style={pageWrap}>
        <div style={card}>
          <h2 style={{ marginTop: 0 }}>Platform Admin</h2>
          <p>You do not have access to this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <div style={card}>
        <h1 style={{ marginTop: 0 }}>Platform Admin</h1>
        <p style={{ color: "#475569" }}>
          Manage schools, contacts, plans, payment status, and platform health.
        </p>

        <div style={summaryGrid}>
          <div style={summaryCard}>
            <strong>Total Schools</strong>
            <div style={summaryValue}>{summary.totalSchools}</div>
          </div>
          <div style={summaryCard}>
            <strong>Active</strong>
            <div style={summaryValue}>{summary.activeSchools}</div>
          </div>
          <div style={summaryCard}>
            <strong>Paid</strong>
            <div style={summaryValue}>{summary.paidSchools}</div>
          </div>
        </div>

        {loading ? (
          <p>Loading schools...</p>
        ) : error ? (
          <p style={{ color: "#b91c1c" }}>{error}</p>
        ) : schools.length === 0 ? (
          <p>No schools found.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={th}>School</th>
                  <th style={th}>School ID</th>
                  <th style={th}>Primary Admin</th>
                  <th style={th}>Email</th>
                  <th style={th}>Plan</th>
                  <th style={th}>Payment</th>
                  <th style={th}>Status</th>
                  <th style={th}>Students</th>
                  <th style={th}>Staff</th>
                  <th style={th}>Tickets</th>
                  <th style={th}>Created</th>
                  <th style={th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {schools.map((school) => (
                  <tr key={school.id}>
                    <td style={td}>{school.name || "Unnamed School"}</td>
                    <td style={td}>{school.id}</td>
                    <td style={td}>{school.primaryAdminName || "—"}</td>
                    <td style={td}>{school.primaryAdminEmail || "—"}</td>
                    <td style={td}>{school.plan || "—"}</td>
                    <td style={td}>{school.paymentStatus || "—"}</td>
                    <td style={td}>{school.status || "—"}</td>
                    <td style={td}>{school.students}</td>
                    <td style={td}>{school.staff}</td>
                    <td style={td}>{school.ticketsCount}</td>
                    <td style={td}>
                      {school.createdAt?.toDate?.().toLocaleDateString?.() || "—"}
                    </td>
                    <td style={td}>
  <div style={{ display: "flex", gap: "0.5rem" }}>
    <button
      onClick={() =>
        togglePaymentStatus(school.id, school.paymentStatus)
      }
      style={{
        background:
          school.paymentStatus === "active" ? "#ef4444" : "#10b981",
        color: "#fff",
        border: "none",
        borderRadius: "8px",
        padding: "0.5rem 0.8rem",
        cursor: "pointer",
        fontWeight: 600,
      }}
    >
      {school.paymentStatus === "active" ? "Deactivate" : "Activate"}
    </button>

    <button
      onClick={() => handleDeleteSchool(school)}
      disabled={workingSchoolId === school.id}
      style={deleteButton}
    >
      {workingSchoolId === school.id ? "Deleting..." : "Delete School"}
    </button>
  </div>
</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const pageWrap = {
  maxWidth: "1400px",
  margin: "2rem auto",
  padding: "0 1rem",
  fontFamily: "'Segoe UI', sans-serif",
};

const card = {
  background: "#fff",
  borderRadius: "14px",
  padding: "1.5rem",
  boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
};

const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: "1rem",
  margin: "1rem 0 1.5rem",
};

const summaryCard = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
  padding: "1rem",
};

const summaryValue = {
  fontSize: "1.8rem",
  fontWeight: 700,
  marginTop: "0.5rem",
};

const table = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: "0.95rem",
};

const th = {
  textAlign: "left",
  padding: "0.75rem",
  borderBottom: "2px solid #e2e8f0",
  background: "#f8fafc",
  whiteSpace: "nowrap",
};

const td = {
  padding: "0.75rem",
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "top",
};

const deleteButton = {
  background: "#dc2626",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "0.5rem 0.8rem",
  cursor: "pointer",
  fontWeight: 600,
};

export default PlatformAdmin;